import React, { useState } from "react";
import { DOMESTIC_PACKAGES, INTERNATIONAL_PACKAGES } from "@/data/site";
import { Clock, IndianRupee, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

function PackageCard({ p, i, kind }) {
  const scrollToContact = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <Reveal delay={i * 70}>
      <div
        data-testid={`${kind}-package-${i}`}
        className="card-lift bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_4px_24px_-8px_rgba(11,61,145,0.15)] flex flex-col h-full"
      >
        <div className="relative zoom-wrap h-56 overflow-hidden">
          <img src={p.img} alt={p.title} loading="lazy" className="zoom-img w-full h-full object-cover" />
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#F9B233] text-[#0B3D91] text-[11px] font-bold uppercase tracking-wide">
            {kind === "domestic" ? "Domestic" : "International"}
          </span>
        </div>
        <div className="p-5 md:p-6 flex-1 flex flex-col">
          <h3 className="text-lg font-bold text-[#0B3D91] tracking-tight">{p.title}</h3>
          <div className="mt-2 flex items-center gap-2 text-sm text-[#555]">
            <Clock className="w-4 h-4 text-[#4DA8FF]" />
            <span>{p.duration}</span>
          </div>
          {p.highlights && (
            <ul className="mt-4 space-y-1.5">
              {p.highlights.map(h => (
                <li key={h} className="flex items-start gap-2 text-sm text-[#444]">
                  <Sparkles className="w-3.5 h-3.5 text-[#F9B233] mt-0.5 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Starting from</p>
              <p className="text-xl font-bold text-[#0B3D91] flex items-center">
                <IndianRupee className="w-4 h-4" />{p.price.toLocaleString("en-IN")}
              </p>
            </div>
            <Button
              onClick={scrollToContact}
              data-testid={`${kind}-book-btn-${i}`}
              className="bg-[#0B3D91] hover:bg-[#082d6d] text-white rounded-full h-10 px-5 font-semibold shadow hover:shadow-lg hover:scale-[1.03] transition-all"
            >
              Book Now
            </Button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Packages() {
  return (
    <section id="packages" data-testid="packages-section" className="py-20 md:py-28 px-5 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Tour Packages"
          title="Curated escapes at every budget"
          subtitle="Whether you're planning a weekend break or a two-week international holiday — we have you covered."
        />

        <Tabs defaultValue="domestic" className="mt-10">
          <TabsList data-testid="packages-tabs" className="mx-auto grid w-full max-w-md grid-cols-2 rounded-full bg-[#F1F5FB] p-1">
            <TabsTrigger
              value="domestic"
              data-testid="tab-domestic"
              className="rounded-full data-[state=active]:bg-[#0B3D91] data-[state=active]:text-white font-semibold"
            >
              Domestic
            </TabsTrigger>
            <TabsTrigger
              value="international"
              data-testid="tab-international"
              className="rounded-full data-[state=active]:bg-[#0B3D91] data-[state=active]:text-white font-semibold"
            >
              International
            </TabsTrigger>
          </TabsList>

          <TabsContent value="domestic" className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {DOMESTIC_PACKAGES.map((p, i) => <PackageCard key={p.title} p={p} i={i} kind="domestic" />)}
            </div>
          </TabsContent>

          <TabsContent value="international" className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {INTERNATIONAL_PACKAGES.map((p, i) => <PackageCard key={p.title} p={p} i={i} kind="international" />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
